import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import CharacterCount from '@tiptap/extension-character-count';
import { Markdown } from 'tiptap-markdown';
import {
  Send, Loader2, Plus, ChevronLeft, Cpu, Settings, X,
  List, FileText, AlertTriangle, Trash2, Download, ThumbsUp, ThumbsDown,
  CircleDot, PenLine, Map, Globe2, Swords, Users, Search,
  Film, Lightbulb, FileInput,
} from 'lucide-react';
import api from '../utils/api';
import Review from './Review';

const API = '/agent';

function normalizeAssistantUiText(text = '') {
  return text
    .replace(/\u2705\s*/g, '')
    .replace(/\u26A0\uFE0F?\s*/g, '提示：')
    .replace(/\p{Extended_Pictographic}\uFE0F?/gu, '');
}

// ── 小工具 ─────────────────────────────────────────────────
function Badge({ children, color = 'gray' }) {
  const map = {
    gray:   'bg-[#eee9de] text-stone-500 dark:bg-white/[0.06] dark:text-gray-400',
    violet: 'bg-[#eee9de] text-[#6f6196] dark:bg-white/[0.08] dark:text-[#b9afe7]',
    emerald:'bg-[#e7eee8] text-[#5e7c76] dark:bg-white/[0.08] dark:text-[#9fc6bd]',
    amber:  'bg-[#f2eadb] text-[#7f6b45] dark:bg-white/[0.08] dark:text-[#d4c7a8]',
    red:    'bg-red-100 text-red-600 dark:bg-red-500/15 dark:text-red-400',
  };
  return (
    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-md ${map[color]}`}>
      {children}
    </span>
  );
}

const AGENT_LABEL = {
  bible: { label: '创意', color: 'violet' },
  outline: { label: '大纲', color: 'emerald' },
  script: { label: '剧本', color: 'amber' },
  consistency: { label: '检查', color: 'red' },
  chat: { label: '对话', color: 'gray' },
};

const EP_STATUS_MAP = {
  outline:  { label: '大纲', color: 'gray' },
  draft:    { label: '草稿', color: 'amber' },
  refined:  { label: '精修', color: 'emerald' },
  final:    { label: '定稿', color: 'violet' },
};

function titleFromIdea(text) {
  const clean = (text || '').replace(/\s+/g, ' ').trim();
  if (!clean) return '';
  return clean.length > 18 ? `${clean.slice(0, 18)}…` : clean;
}

// ── 项目列表页 ─────────────────────────────────────────────
function ProjectList({ onOpen, initialIdea = '', initialBrief = '', initialTab = 'scripts' }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(initialTab === 'review' ? 'review' : 'scripts');   // scripts | review
  const [busy, setBusy] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState('');

  useEffect(() => {
    api.get(`${API}/projects`)
      .then(r => setProjects(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // 新建三模式：从创意 / 项目改编 / 空白 —— 创建项目后进工作台
  const createAndOpen = async (mode) => {
    if (mode === 'adapt') return; // 项目改编即将上线，按钮已禁用
    setBusy(true);
    try {
      const title = titleFromIdea(initialIdea) || '未命名作品';
      const r = await api.post(`${API}/projects`, { title });
      setProjects(prev => [r.data, ...prev]);
      onOpen(r.data);
    } catch (e) {
      alert(e.response?.data?.error || '创建失败');
    } finally {
      setBusy(false);
    }
  };

  const deleteProject = async (id, e) => {
    e?.stopPropagation?.();
    if (!window.confirm('确定删除这个项目？项目内所有集数和对话记录将永久删除。')) return;
    await api.delete(`${API}/projects/${id}`).catch(() => {});
    setProjects(prev => prev.filter(p => p.id !== id));
  };

  const stageMap = { bible: '创意', outline: '大纲', writing: '写作中', script: '写作中', review: '完善' };
  const shown = query.trim()
    ? projects.filter(p => (p.title || '').toLowerCase().includes(query.trim().toLowerCase()))
    : projects;

  return (
    <div className="min-h-full bg-[#f6f3ec] dark:bg-[#0e0e10]">
      <header className="border-b border-[#ded6c8] bg-[#fbfaf6] px-8 pt-5 dark:border-white/[0.06] dark:bg-[#0c0c10]">
        <h1 className="text-[22px] font-bold text-stone-900 dark:text-gray-100">我的剧本</h1>
        <p className="mt-1 text-[13px] text-stone-400">{projects.length} 部作品</p>
        <div className="mt-4 flex items-center gap-6">
          {[['scripts', '剧本创作'], ['review', '审核评分']].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)}
              className={`pb-3 text-[14px] transition-colors ${tab === k ? 'border-b-2 border-[#8c7db0] font-medium text-stone-900 dark:text-gray-100' : 'text-stone-400 hover:text-stone-700 dark:text-gray-500 dark:hover:text-gray-300'}`}>
              {l}
            </button>
          ))}
          {tab === 'scripts' && (searchOpen ? (
            <div className="mb-1.5 ml-auto flex items-center gap-1.5">
              <input autoFocus value={query} onChange={e => setQuery(e.target.value)} placeholder="搜作品标题…"
                className="h-8 w-48 rounded-lg border border-[#ded6c8] bg-white px-2.5 text-[12px] text-stone-700 outline-none focus:border-[#8c7db0] dark:border-white/[0.1] dark:bg-white/[0.05] dark:text-gray-200" />
              <button aria-label="关闭搜索" onClick={() => { setSearchOpen(false); setQuery(''); }}
                className="flex h-8 w-8 items-center justify-center rounded-lg text-stone-400 transition-colors hover:text-stone-700 dark:text-gray-500"><X size={15} /></button>
            </div>
          ) : (
            <button aria-label="搜索" onClick={() => setSearchOpen(true)}
              className="mb-1.5 ml-auto flex h-8 w-8 items-center justify-center rounded-lg border border-[#ded6c8] text-stone-400 transition-colors hover:text-stone-700 dark:border-white/[0.1] dark:text-gray-500">
              <Search size={15} />
            </button>
          ))}
        </div>
      </header>

      {tab === 'scripts' ? (
        <div className="px-8 py-6">
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 size={20} className="animate-spin text-gray-400" /></div>
          ) : (
            <div className="grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] items-start gap-4">
              {/* 新建作品 */}
              <div className="flex flex-col gap-2.5 rounded-xl border border-[#c9bedc] bg-white p-4 dark:border-white/[0.12] dark:bg-[#14141a]">
                <div className="flex items-center gap-2">
                  <div className="flex h-8 w-8 items-center justify-center rounded-[9px] bg-[#eee9de] text-[#6f6196] dark:bg-white/[0.08] dark:text-[#b9afe7]"><Plus size={17} /></div>
                  <span className="text-[14px] font-medium">新建作品</span>
                </div>
                {initialIdea && <p className="line-clamp-2 rounded-lg bg-[#f6f3ec] px-2.5 py-1.5 text-[11px] text-stone-500 dark:bg-white/[0.04] dark:text-gray-400">{initialIdea}</p>}
                {[['idea', '从创意开始', Lightbulb, false], ['adapt', '项目改编', FileInput, true], ['blank', '空白', FileText, false]].map(([m, l, Ic, soon]) => (
                  <button key={m} onClick={() => createAndOpen(m)} disabled={busy || soon}
                    className={`flex items-center gap-2.5 rounded-[9px] border border-[#ded6c8] px-2.5 py-2 text-left text-[12px] transition-colors dark:border-white/[0.08] ${soon ? 'cursor-not-allowed text-stone-400 dark:text-gray-500' : 'text-stone-600 hover:bg-[#f5f0e7] disabled:opacity-50 dark:text-gray-300 dark:hover:bg-white/[0.04]'}`}>
                    <Ic size={16} className={`shrink-0 ${soon ? 'text-stone-300 dark:text-gray-600' : 'text-[#6f6196] dark:text-[#b9afe7]'}`} />{l}
                    {soon && <span className="ml-auto rounded bg-[#f0ebe2] px-1.5 py-0.5 text-[10px] text-stone-400 dark:bg-white/[0.06] dark:text-gray-500">即将上线</span>}
                  </button>
                ))}
              </div>

              {/* 项目卡 */}
              {shown.map(p => (
                <div key={p.id} onClick={() => onOpen(p)}
                  className="group cursor-pointer overflow-hidden rounded-xl border border-[#ded6c8] bg-white transition-colors hover:border-[#c9bedc] dark:border-white/[0.06] dark:bg-[#14141a]">
                  <div className="relative flex h-[92px] items-end bg-[#f6f3ec] p-3 dark:bg-white/[0.03]">
                    {p.genre && <span className="absolute left-3 top-3"><Badge color="violet">{p.genre}</Badge></span>}
                    <p className="line-clamp-2 text-[14px] font-medium leading-snug text-stone-900 dark:text-gray-100">{p.title}</p>
                  </div>
                  <div className="flex items-center justify-between px-3 py-2.5">
                    <span className="text-[11px] text-stone-400">{p.episode_count || 0}集 · {stageMap[p.current_stage] || '创意'}</span>
                    <div className="flex gap-1">
                      <button aria-label="送进画布" onClick={e => { e.stopPropagation(); alert('送进画布：结构化交接开发中，请先在无限画布里导入本剧正文'); }}
                        className="flex h-7 w-7 items-center justify-center rounded-lg text-stone-400 transition-colors hover:bg-[#f5f0e7] hover:text-[#6f6196] dark:hover:bg-white/[0.06]"><Film size={14} /></button>
                      <button aria-label="删除" onClick={e => deleteProject(p.id, e)}
                        className="flex h-7 w-7 items-center justify-center rounded-lg text-stone-400 transition-colors hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-500/[0.08]"><Trash2 size={14} /></button>
                    </div>
                  </div>
                </div>
              ))}
              {query.trim() && shown.length === 0 && (
                <p className="col-span-full py-8 text-center text-[13px] text-stone-400">没有匹配「{query.trim()}」的作品</p>
              )}
            </div>
          )}
        </div>
      ) : (
        // 审核评分：整套 Review 工具（上传/粘贴/平台剧本 → 打分 → 历史/报告）直接嵌进 tab，不再跳转
        <div className="h-[calc(100vh-116px)]">
          <Review />
        </div>
      )}
    </div>
  );
}

// ── 客户端粗解析"分N集大纲"的总集数（用于大集数确认 + 进度文案；与后端 parseTargetCount 同义，写第N集返回 null）──
function zhNum(s) {
  const c = { 一:1,二:2,两:2,三:3,四:4,五:5,六:6,七:7,八:8,九:9,十:10 };
  if (/^\d+$/.test(s)) return parseInt(s);
  if (!s.includes('十')) return c[s] || null;
  if (s === '十') return 10;
  if (s[0] === '十') return 10 + (c[s[1]] || 0);
  const [a, b] = s.split('十');
  return (c[a] || 1) * 10 + (c[b] || 0);
}
function outlineCount(text = '') {
  if (/第\s*\d+\s*集|写第|写这集|这集/.test(text)) return null; // 单集/写正文，不是大纲集数
  const m = text.match(/(\d+|[一二三四五六七八九十两]+)\s*集/);
  if (!m) return null;
  const n = zhNum(m[1]);
  return (n && n >= 2 && n <= 100) ? n : null;
}

// ── 思考动画气泡 ───────────────────────────────────────────
const THINKING_STEPS = [
  '分析你的故事需求…',
  '查阅短剧创作规则…',
  '构思情节结构…',
  '规划角色设定…',
  '权衡爽点分布…',
  '生成创作方案…',
];

function ThinkingBubble({ task }) {
  const [idx, setIdx] = useState(0);
  const [visible, setVisible] = useState(true);
  const [elapsed, setElapsed] = useState(0);

  // 耗时计时（让用户看到它在动、知道已经等了多久）
  useEffect(() => {
    const t0 = Date.now();
    const et = setInterval(() => setElapsed(Math.floor((Date.now() - t0) / 1000)), 1000);
    return () => clearInterval(et);
  }, []);

  // 没有明确任务时才轮播通用步骤；有任务（大纲/正文）显示确定文案
  useEffect(() => {
    if (task) return;
    const t = setInterval(() => {
      setVisible(false);
      setTimeout(() => { setIdx(i => (i + 1) % THINKING_STEPS.length); setVisible(true); }, 200);
    }, 1800);
    return () => clearInterval(t);
  }, [task]);

  const mmss = `${Math.floor(elapsed / 60)}:${String(elapsed % 60).padStart(2, '0')}`;
  let title = '写作助手思考中';
  let line = THINKING_STEPS[idx];
  if (task?.type === 'outline') {
    title = '正在规划分集大纲';
    line = task.done != null
      ? `已生成 ${task.done}/${task.count} 集大纲…`
      : `正在规划 ${task.count} 集分集大纲（不是正文），约需 1-2 分钟，请稍候…`;
  } else if (task?.type === 'script') {
    title = '正在创作正文';
    line = `正在写第 ${task.ep} 集并通过一致性门禁，约 1-2 分钟…`;
  }
  const pct = task?.type === 'outline' && task.done != null && task.count
    ? Math.min(100, Math.round((task.done / task.count) * 100)) : null;

  return (
    <div className="flex justify-start">
      <div className="bg-[#fbfaf6] dark:bg-white/[0.05] border border-[#ded6c8] dark:border-white/[0.07] px-3 py-2.5 rounded-xl rounded-tl-sm min-w-[200px]">
        <div className="flex items-center gap-1.5 mb-1">
          <Loader2 size={10} className="animate-spin text-[#8c7db0] shrink-0" />
          <span className="text-[10px] font-semibold text-[#6f6196]">{title}</span>
          <span className="text-[10px] text-gray-300 dark:text-gray-500 ml-auto tabular-nums">已用时 {mmss}</span>
        </div>
        <p
          className="text-[11px] text-gray-400 transition-opacity duration-200"
          style={{ opacity: task ? 1 : (visible ? 1 : 0) }}
        >
          {line}
        </p>
        {pct != null && (
          <div className="mt-1.5 h-1 bg-gray-200 dark:bg-white/10 rounded-full overflow-hidden">
            <div className="h-full bg-[#8c7db0] rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
          </div>
        )}
      </div>
    </div>
  );
}

// ── 大纲视图（outline 状态集数）────────────────────────────
function OutlineView({ episode, onWrite }) {
  if (!episode) return (
    <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
      让 AI 生成剧本，或展开项目资料选择集数
    </div>
  );
  const beats = Array.isArray(episode.outline?.beats) ? episode.outline.beats : [];
  const BEAT_COLOR = { '开场钩子':'violet','冲突':'rose','转折':'amber','爽点':'emerald','结尾钩子':'violet' };
  return (
    <div className="flex flex-col h-full">
      <div className="shrink-0 px-5 py-2.5 border-b border-[#ded6c8] dark:border-white/[0.06] bg-[#fbfaf6] dark:bg-[#0c0c10] flex items-center gap-2">
        <FileText size={14} className="text-[#5e7c76]" />
        <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">
          第{episode.episode_number}集《{episode.title || '未命名'}》
        </span>
        <Badge color="gray">大纲</Badge>
      </div>
      <div className="flex-1 overflow-y-auto px-8 py-6 bg-[#fbfaf6] dark:bg-[#14141a] space-y-4">
        {episode.opening_hook && (
          <div className="bg-[#eee9de] dark:bg-white/[0.08] border border-[#ded6c8] dark:border-white/[0.12] rounded-xl p-3">
            <p className="text-[10px] font-semibold text-[#6f6196] mb-1 flex items-center gap-1"><CircleDot size={10} />开场钩子</p>
            <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{episode.opening_hook}</p>
          </div>
        )}
        {beats.length > 0 && (
          <div>
            <p className="text-[11px] font-semibold text-gray-400 mb-2 uppercase tracking-wide">节拍结构</p>
            <div className="space-y-2">
              {beats.map((beat, i) => {
                const c = BEAT_COLOR[beat.type] || 'gray';
                return (
                  <div key={i} className="flex gap-3 items-start">
                    <Badge color={c}>{beat.type}</Badge>
                    <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed flex-1">{beat.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {episode.ending_hook && (
          <div className="bg-[#f2eadb] dark:bg-white/[0.08] border border-[#e0d3b9] dark:border-white/[0.12] rounded-xl p-3">
            <p className="text-[10px] font-semibold text-[#7f6b45] mb-1 flex items-center gap-1"><CircleDot size={10} />结尾钩子</p>
            <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{episode.ending_hook}</p>
          </div>
        )}
        {episode.summary && (
          <div className="border-t border-gray-100 dark:border-white/[0.06] pt-3">
            <p className="text-[10px] font-semibold text-gray-400 mb-1">一句话摘要</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">{episode.summary}</p>
          </div>
        )}
        <div className="pt-2">
          <button
            onClick={() => onWrite(episode.episode_number)}
            className="w-full py-2.5 bg-[#8c7db0] hover:bg-[#7d6ca3] text-white text-sm font-semibold rounded-xl transition-colors flex items-center justify-center gap-1.5"
          >
            <PenLine size={13} />让写作助手写这集剧本
          </button>
        </div>
      </div>
    </div>
  );
}

// ── 创意字段编辑器（世界观 / 核心矛盾 / 主题）─────────────
function BibleFieldEditor({ field, projectId, onSaved }) {
  const [value, setValue] = useState(field.value || '');
  const [saving, setSaving] = useState(false);
  const displayLabel = normalizeAssistantUiText(field.label);
  async function save() {
    setSaving(true);
    try {
      await api.patch(`${API}/projects/${projectId}/bible`, { key: field.key, value });
      onSaved(field.key, value);
    } catch (e) { alert('保存失败：' + e.message); }
    finally { setSaving(false); }
  }
  return (
    <div className="flex flex-col h-full">
      <div className="shrink-0 px-5 py-2.5 border-b border-[#ded6c8] dark:border-white/[0.06] bg-[#fbfaf6] dark:bg-[#0c0c10] flex items-center gap-2">
        <FileText size={14} className="text-[#6f6196]" />
        <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">{displayLabel}</span>
      </div>
      <div className="flex-1 overflow-y-auto p-6 bg-[#fbfaf6] dark:bg-[#14141a]">
        <textarea
          className="w-full h-full min-h-[400px] text-sm text-gray-800 dark:text-gray-200 bg-transparent resize-none outline-none leading-relaxed placeholder:text-gray-400"
          value={value}
          onChange={e => setValue(e.target.value)}
          placeholder={`在这里编写${displayLabel}…`}
        />
      </div>
      <div className="shrink-0 px-5 py-3 border-t border-[#ded6c8] dark:border-white/[0.06] bg-[#fbfaf6] dark:bg-[#0c0c10]">
        <button onClick={save} disabled={saving}
          className="px-4 py-1.5 bg-[#8c7db0] hover:bg-[#7d6ca3] disabled:opacity-40 text-white text-xs font-semibold rounded-lg transition-colors">
          {saving ? '保存中…' : '保存'}
        </button>
      </div>
    </div>
  );
}

// ── 人物小传编辑器 ─────────────────────────────────────────
function CharacterEditor({ charIndex, projectId, bible, onSaved }) {
  const chars = Array.isArray(bible?.characters) ? bible.characters : [];
  const [data, setData] = useState({ ...chars[charIndex] });
  const [saving, setSaving] = useState(false);
  const F = ({ label, field, multiline, placeholder }) => (
    <div>
      <p className="text-[10px] font-semibold text-gray-400 mb-1">{label}</p>
      {multiline
        ? <textarea rows={3} className="w-full text-sm text-gray-800 dark:text-gray-200 bg-[#f7f3eb] dark:bg-white/[0.04] border border-[#ded6c8] dark:border-white/[0.08] rounded-lg px-3 py-2 resize-none outline-none focus:border-[#8c7db0] transition-colors leading-relaxed"
            placeholder={placeholder || ''} value={data[field] || ''} onChange={e => setData({ ...data, [field]: e.target.value })} />
        : <input className="w-full text-sm text-gray-800 dark:text-gray-200 bg-[#f7f3eb] dark:bg-white/[0.04] border border-[#ded6c8] dark:border-white/[0.08] rounded-lg px-3 py-2 outline-none focus:border-[#8c7db0] transition-colors"
            placeholder={placeholder || ''} value={data[field] || ''} onChange={e => setData({ ...data, [field]: e.target.value })} />}
    </div>
  );
  const ArrF = ({ label, field }) => (
    <div>
      <p className="text-[10px] font-semibold text-gray-400 mb-1">{label}<span className="font-normal ml-1 text-gray-300">（用顿号分隔）</span></p>
      <input className="w-full text-sm text-gray-800 dark:text-gray-200 bg-[#f7f3eb] dark:bg-white/[0.04] border border-[#ded6c8] dark:border-white/[0.08] rounded-lg px-3 py-2 outline-none focus:border-[#8c7db0] transition-colors"
        value={(data[field] || []).join('、')}
        onChange={e => setData({ ...data, [field]: e.target.value.split('、').map(s => s.trim()).filter(Boolean) })} />
    </div>
  );
  async function save() {
    setSaving(true);
    try {
      const newChars = chars.map((c, i) => i === charIndex ? data : c);
      await api.patch(`${API}/projects/${projectId}/bible`, { key: 'characters', value: newChars });
      onSaved('characters', newChars);
    } catch (e) { alert('保存失败：' + e.message); }
    finally { setSaving(false); }
  }
  return (
    <div className="flex flex-col h-full">
      <div className="shrink-0 px-5 py-2.5 border-b border-[#ded6c8] dark:border-white/[0.06] bg-[#fbfaf6] dark:bg-[#0c0c10] flex items-center gap-2">
        <FileText size={14} className="text-[#6f6196]" />
        <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">人物小传 · {data.name || '未命名'}</span>
      </div>
      <div className="flex-1 overflow-y-auto px-6 py-5 bg-[#fbfaf6] dark:bg-[#14141a] space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <F label="姓名" field="name" />
          <F label="身份 / 角色" field="role" />
        </div>
        <F label={'\u5916\u89c2\u63cf\u8ff0'} field="appearance" multiline placeholder={'\u5e74\u9f84\u6bb5\u3001\u4f53\u6001/\u8138\u90e8\u6c14\u8d28\u3001\u53d1\u578b\u670d\u88c5\u3001\u6807\u5fd7\u7269\u6216\u89c6\u89c9\u9535\u70b9'} />
        <F label={'\u8eab\u4efd\u63cf\u8ff0'} field="identity_description" multiline placeholder={'\u804c\u4e1a/\u9636\u5c42/\u5bb6\u65cf\u4f4d\u7f6e/\u516c\u5f00\u8eab\u4efd/\u9690\u85cf\u538b\u529b\u53ca\u6545\u4e8b\u529f\u80fd'} />
        <F label={'\u6027\u683c\u63cf\u8ff0'} field="personality_description" multiline placeholder={'\u7528\u4e60\u60ef\u52a8\u4f5c\u3001\u51b3\u7b56\u65b9\u5f0f\u3001\u538b\u529b\u53cd\u5e94\u5199\u6027\u683c\uff0c\u4e0d\u5806\u5f62\u5bb9\u8bcd'} />
        <F label="人物标签" field="dramatic_tag" placeholder="一句话定义，如「表面温柔实为腹黑的复仇者」" />
        <F label="行动型性格" field="action_driven_personality" multiline placeholder="通过行为体现，如「被羞辱时冷笑着反手打脸，从不解释只用结果说话」" />
        <F label="高光时刻" field="highlight_moment" multiline placeholder="最令观众热血沸腾的场面" />
        <F label="口头禅 / 标志台词" field="catchphrase" placeholder="角色标志性台词" />
        <F label="背景故事" field="background" multiline />
        <F label="人物弧光（成长轨迹）" field="arc" multiline />
        <ArrF label="视觉标签" field="visual_anchors" />
        <ArrF label="禁忌行为" field="taboos" />
      </div>
      <div className="shrink-0 px-5 py-3 border-t border-[#ded6c8] dark:border-white/[0.06] bg-[#fbfaf6] dark:bg-[#0c0c10]">
        <button onClick={save} disabled={saving}
          className="px-4 py-1.5 bg-[#8c7db0] hover:bg-[#7d6ca3] disabled:opacity-40 text-white text-xs font-semibold rounded-lg transition-colors">
          {saving ? '保存中…' : '保存'}
        </button>
      </div>
    </div>
  );
}

// ── 故事大纲（treatment：整体故事蓝图，可手动编辑）───────────────
function TreatmentView({ treatment, onSave }) {
  const [text, setText] = useState(treatment || '');
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  useEffect(() => { setText(treatment || ''); setDirty(false); }, [treatment]);

  const save = async () => {
    setSaving(true);
    try { await onSave(text); setDirty(false); }
    catch (e) { alert('保存失败：' + (e.response?.data?.error || e.message)); }
    finally { setSaving(false); }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="shrink-0 px-5 py-2.5 border-b border-[#ded6c8] dark:border-white/[0.06] bg-[#fbfaf6] dark:bg-[#0c0c10] flex items-center gap-2">
        <Map size={14} className="text-[#6f6196]" />
        <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">故事大纲</span>
        <span className="text-[10px] text-gray-400">可直接编辑</span>
        {dirty && <span className="text-[10px] text-[#7f6b45]">未保存</span>}
        <button onClick={save} disabled={saving || !dirty}
          className="ml-auto px-3 py-1 rounded-lg bg-[#8c7db0] hover:bg-[#7d6ca3] disabled:opacity-40 text-white text-[11px] font-medium flex items-center gap-1">
          {saving ? <Loader2 size={11} className="animate-spin" /> : null}保存
        </button>
      </div>
      <div className="flex-1 overflow-y-auto px-8 py-6 bg-[#fbfaf6] dark:bg-[#14141a]">
        <textarea
          value={text}
          onChange={e => { setText(e.target.value); setDirty(true); }}
          placeholder="还没有故事大纲。在左侧让写作助手「生成故事大纲」，或直接在这里手写。"
          className="w-full min-h-[420px] bg-transparent resize-none outline-none text-[13px] text-gray-800 dark:text-gray-200 leading-relaxed whitespace-pre-wrap font-sans"
        />
      </div>
    </div>
  );
}

// ── 门禁拦截：原稿 vs 候选稿 对比二选一 ───────────────────────
function CandidateCompareView({ projectId, candidate, onAccept, onKeep }) {
  const [original, setOriginal] = useState(null);
  const [accepting, setAccepting] = useState(false);
  useEffect(() => {
    api.get(`${API}/projects/${projectId}/episodes/${candidate.episode_number}`).then(r => setOriginal(r.data)).catch(() => {});
  }, [projectId, candidate.episode_number]);
  const critical = (candidate.issues || []).filter(i => i.severity === 'critical');
  const hasOriginal = !!(original?.content && original.content.trim());
  return (
    <div className="flex flex-col h-full">
      <div className="shrink-0 px-5 py-2.5 border-b border-[#e0d3b9] dark:border-white/[0.06] bg-[#f2eadb] dark:bg-white/[0.08] flex items-center gap-2">
        <AlertTriangle size={14} className="text-[#7f6b45]" />
        <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">第{candidate.episode_number}集 · 门禁拦截，请选择</span>
        <span className="text-[11px] text-[#7f6b45] dark:text-[#d4c7a8]">候选稿仍有 {critical.length} 处严重问题，已拦截未入库{hasOriginal ? '，未覆盖原稿' : ''}</span>
      </div>
      {critical.length > 0 && (
        <div className="shrink-0 px-5 py-2 border-b border-[#e4dccf] dark:border-white/[0.06] bg-[#fbfaf6] dark:bg-[#14141a] space-y-1 max-h-28 overflow-y-auto">
          {critical.map((it, i) => (
            <p key={i} className="text-[11px] text-red-500 dark:text-red-400">• {it.description || (it.fact_violated ? `与既成事实冲突：${it.fact_violated}` : '严重一致性问题')}{it.suggestion ? <span className="text-gray-400"> — {it.suggestion}</span> : null}</p>
          ))}
        </div>
      )}
      <div className="flex-1 min-h-0 grid grid-cols-2 divide-x divide-[#ded6c8] dark:divide-white/[0.06]">
        <div className="overflow-y-auto px-5 py-4 bg-[#fbfaf6] dark:bg-[#14141a]">
          <p className="text-[11px] font-semibold text-gray-400 mb-2">{hasOriginal ? '原稿（保留则不变）' : '本集暂无正式稿'}</p>
          <div className="whitespace-pre-wrap text-[12px] text-gray-700 dark:text-gray-300 leading-relaxed">{hasOriginal ? original.content : '（这一集还没有通过门禁的正式正文）'}</div>
        </div>
        <div className="overflow-y-auto px-5 py-4 bg-[#fbfaf6] dark:bg-[#14141a]">
          <p className="text-[11px] font-semibold text-[#6f6196] mb-2">候选稿《{candidate.title || '未命名'}》<span className="text-[10px] text-[#7f6b45] font-normal ml-1">门禁未过</span></p>
          <div className="whitespace-pre-wrap text-[12px] text-gray-700 dark:text-gray-300 leading-relaxed">{candidate.content}</div>
        </div>
      </div>
      <div className="shrink-0 flex gap-2 px-5 py-3 border-t border-[#ded6c8] dark:border-white/[0.06] bg-[#fbfaf6] dark:bg-[#0c0c10]">
        <button onClick={onKeep}
          className="px-4 py-2 rounded-xl border border-[#ded6c8] dark:border-white/[0.1] text-gray-600 dark:text-gray-300 text-[13px] font-medium hover:bg-[#f7f3eb] dark:hover:bg-white/[0.04]">
          {hasOriginal ? '保留原稿' : '放弃（稍后重写）'}
        </button>
        <button onClick={async () => { setAccepting(true); try { await onAccept(); } finally { setAccepting(false); } }} disabled={accepting}
          className="flex-1 py-2 rounded-xl bg-[#8c7db0] hover:bg-[#7d6ca3] disabled:opacity-40 text-white text-[13px] font-medium flex items-center justify-center gap-2">
          {accepting ? <Loader2 size={13} className="animate-spin" /> : null}仍采用候选稿（强制保存第{candidate.episode_number}集）
        </button>
      </div>
    </div>
  );
}

// ── 故事大纲总览（所有集数大纲聚合）─────────────────────────
function StoryOutlineView({ episodes }) {
  return (
    <div className="flex flex-col h-full">
      <div className="shrink-0 px-5 py-2.5 border-b border-[#ded6c8] dark:border-white/[0.06] bg-[#fbfaf6] dark:bg-[#0c0c10] flex items-center gap-2">
        <FileText size={14} className="text-[#5e7c76]" />
        <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">分集大纲</span>
        <Badge color="gray">{episodes.length} 集</Badge>
      </div>
      <div className="flex-1 overflow-y-auto px-6 py-5 bg-[#fbfaf6] dark:bg-[#14141a] space-y-4">
        {episodes.map(ep => {
          const beats = Array.isArray(ep.outline?.beats) ? ep.outline.beats : [];
          return (
            <div key={ep.episode_number} className="border border-[#ded6c8] dark:border-white/[0.06] rounded-xl p-3 bg-[#fcfbf8] dark:bg-white/[0.02]">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-bold text-[#6f6196] dark:text-[#b9afe7]">第{ep.episode_number}集</span>
                <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">《{ep.title || '未命名'}》</span>
              </div>
              {ep.summary && <p className="text-[11px] text-gray-600 dark:text-gray-300 leading-relaxed mb-2">{ep.summary}</p>}
              {ep.opening_hook && <p className="text-[11px] text-[#6f6196] mb-1 flex items-start gap-1"><CircleDot size={10} className="mt-0.5 shrink-0" />{ep.opening_hook}</p>}
              {beats.length > 0 && (
                <div className="space-y-0.5 mb-1">
                  {beats.map((b, i) => (
                    <p key={i} className="text-[11px] text-gray-500 dark:text-gray-400">
                      <span className="font-semibold text-gray-600 dark:text-gray-300 mr-1">[{b.type}]</span>{b.desc}
                    </p>
                  ))}
                </div>
              )}
              {ep.ending_hook && <p className="text-[11px] text-[#7f6b45] flex items-start gap-1"><CircleDot size={10} className="mt-0.5 shrink-0" />{ep.ending_hook}</p>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Tiptap 脚本编辑器 ──────────────────────────────────────
function ScriptEditor({ episode, onSave, onRename }) {
  const [charCount, setCharCount] = useState(0);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleVal, setTitleVal] = useState('');
  const skipSave = useRef(false); // 程序化载入内容时为 true，避免误触发自动保存写错集

  const editor = useEditor({
    extensions: [
      StarterKit,
      Markdown.configure({ html: false, transformPastedText: true, transformCopiedText: true }),
      Placeholder.configure({ placeholder: '这里是剧本正文…从左侧对话区让 AI 生成，或直接在这里编辑' }),
      CharacterCount,
    ],
    content: episode?.content || '',
    editorProps: {
      attributes: {
        class: 'prose prose-sm dark:prose-invert max-w-none outline-none min-h-[300px] text-gray-800 dark:text-gray-200 leading-relaxed',
      },
    },
    onUpdate: ({ editor }) => {
      setCharCount(editor.storage.characterCount.characters());
      if (skipSave.current) { skipSave.current = false; return; } // 程序化载入，不保存
      if (onSave) {
        const md = editor.storage.markdown.getMarkdown();
        onSave(md, editor.getHTML());
      }
    },
  });

  useEffect(() => {
    if (editor && episode?.content !== undefined) {
      const current = editor.storage.markdown?.getMarkdown() ?? editor.getText();
      if (current !== episode.content) {
        skipSave.current = true; // 这次内容变化是程序载入，不触发自动保存
        editor.commands.setContent(episode.content || '');
        setTimeout(() => setCharCount(editor.storage.characterCount.characters()), 0);
      }
    }
  }, [episode?.content, editor]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="flex flex-col h-full">
      <div className="shrink-0 px-5 py-2.5 border-b border-[#ded6c8] dark:border-white/[0.06] bg-[#fbfaf6] dark:bg-[#0c0c10] flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <FileText size={14} className="text-[#5e7c76] shrink-0" />
          {!episode ? (
            <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">让 AI 生成剧本，或展开项目资料选择集数</span>
          ) : editingTitle ? (
            <span className="text-sm font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-1">
              第{episode.episode_number}集《
              <input
                autoFocus
                value={titleVal}
                onChange={e => setTitleVal(e.target.value)}
                onBlur={() => { onRename && onRename(episode.episode_number, titleVal.trim()); setEditingTitle(false); }}
                onKeyDown={e => {
                  if (e.key === 'Enter') { onRename && onRename(episode.episode_number, titleVal.trim()); setEditingTitle(false); }
                  if (e.key === 'Escape') setEditingTitle(false);
                }}
                className="bg-transparent border-b border-[#8c7db0] focus:outline-none w-28 text-sm font-semibold"
              />》
            </span>
          ) : (
            <button
              onClick={() => { setTitleVal(episode.title || ''); setEditingTitle(true); }}
              title="点击改集名"
              className="text-sm font-semibold text-gray-800 dark:text-gray-200 hover:text-[#6f6196] dark:hover:text-[#b9afe7] transition-colors truncate"
            >
              第{episode.episode_number}集《{episode.title || '未命名'}》
            </button>
          )}
          {episode && <Badge color={EP_STATUS_MAP[episode.status]?.color}>{EP_STATUS_MAP[episode.status]?.label || episode.status}</Badge>}
        </div>
        <span className="text-[11px] text-gray-400">{charCount.toLocaleString()} 字</span>
      </div>
      {episode?.verification && episode.verification.passed === false && (
        <div className="shrink-0 px-5 py-2 bg-[#f2eadb] dark:bg-white/[0.08] border-b border-[#e0d3b9] dark:border-white/[0.12]">
          <p className="text-[11px] font-semibold text-[#7f6b45] dark:text-[#d4c7a8] flex items-center gap-1">
            <AlertTriangle size={11} />
            此集门禁未通过{episode.verification.accepted_by_user ? '（你已选择采用）' : ''}，有 {(episode.verification.issues || []).filter(i => i.severity === 'critical').length} 处严重一致性问题，建议「重写第{episode.episode_number}集」
          </p>
          {(episode.verification.issues || []).filter(i => i.severity === 'critical').slice(0, 3).map((it, i) => (
            <p key={i} className="text-[10px] text-[#7f6b45]/80 dark:text-[#d4c7a8]/80 mt-0.5">• {it.description || (it.fact_violated ? `与既成事实冲突：${it.fact_violated}` : '严重一致性问题')}</p>
          ))}
        </div>
      )}
      <div className="flex-1 overflow-y-auto px-8 py-6 bg-[#fbfaf6] dark:bg-[#14141a]">
        {episode ? (
          <EditorContent editor={editor} />
        ) : (
          <div className="text-gray-400 text-sm text-center py-20">让 AI 生成剧本，或展开项目资料选择集数</div>
        )}
      </div>
    </div>
  );
}

// ── 主工作区 ───────────────────────────────────────────────
function ProjectWorkspace({ projectId, onBack, autoSend }) {
  const [project, setProject] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [activeEpisode, setActiveEpisode] = useState(null);
  const [episodeContent, setEpisodeContent] = useState(null);
  const [consistencyIssues, setConsistencyIssues] = useState([]);
  const [activeContent, setActiveContent] = useState(null);
  // null=episode模式  {type:'bible',key,label,value}  {type:'character',index}  {type:'story-outline'}
  const [chatWidth, setChatWidth] = useState(340);
  const [leftWidth, setLeftWidth] = useState(258);
  const [showRightPanel, setShowRightPanel] = useState(true);
  const [epView, setEpView] = useState('script');           // 中栏当前集视图：outline | script
  const [settingTab, setSettingTab] = useState('creative');  // 左栏"全剧设定"子tab：creative | facts
  const [agentTypeHint, setAgentTypeHint] = useState(null);
  const [pendingTask, setPendingTask] = useState(null); // 进度气泡任务态 {type:'outline'|'script', count?, ep?, done?}
  const [pendingCandidate, setPendingCandidate] = useState(null); // 门禁拦下的候选稿（原稿vs候选对比）
  const [factCandidates, setFactCandidates] = useState([]);        // 待确认事实
  const [factStats, setFactStats] = useState({ pending: 0, confirmed: 0 });
  const [showSettings, setShowSettings] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editGenre, setEditGenre] = useState('');
  const [editFactMode, setEditFactMode] = useState('auto');
  const [savingSettings, setSavingSettings] = useState(false);
  const chatRef = useRef(null);
  const saveTimer = useRef(null);
  const inputRef = useRef(null);

  function startDrag(side, e) {
    e.preventDefault();
    const startX = e.clientX;
    // 布局用 flex-row-reverse：DOM 'left' 手柄在视觉右侧(改 chat 宽)，'right' 手柄在视觉左侧(改左栏宽)
    const startW = side === 'left' ? chatWidth : leftWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    function onMove(ev) {
      const dx = ev.clientX - startX;
      if (side === 'left') setChatWidth(Math.max(280, Math.min(520, startW - dx)));
      else setLeftWidth(Math.max(200, Math.min(420, startW + dx)));
    }
    function onUp() {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    }
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }

  const loadProject = useCallback(async () => {
    try {
      const [projR, chatR] = await Promise.all([
        api.get(`${API}/projects/${projectId}`),
        api.get(`${API}/projects/${projectId}/chat`),
      ]);
      setProject(projR.data);
      setMessages(chatR.data);
    } catch (e) {
      console.error(e);
    }
  }, [projectId]);

  const loadFacts = useCallback(async () => {
    const [c, s] = await Promise.all([
      api.get(`${API}/projects/${projectId}/fact-candidates`).catch(() => ({ data: [] })),
      api.get(`${API}/projects/${projectId}/fact-stats`).catch(() => ({ data: { pending: 0, confirmed: 0 } })),
    ]);
    setFactCandidates(c.data || []);
    setFactStats(s.data || { pending: 0, confirmed: 0 });
  }, [projectId]);

  useEffect(() => { loadProject(); loadFacts(); }, [loadProject, loadFacts]);

  // 大纲分批生成时：每 3s 轮询集数，驱动进度条 X/N（后端逐批落库，前端能看到边出边涨）
  useEffect(() => {
    if (pendingTask?.type !== 'outline') return;
    let alive = true;
    const poll = async () => {
      try {
        const r = await api.get(`${API}/projects/${projectId}`);
        const done = (r.data.episodes || []).length;
        if (alive) setPendingTask(t => (t && t.type === 'outline') ? { ...t, done } : t);
      } catch {}
    };
    const iv = setInterval(poll, 3000);
    poll();
    return () => { alive = false; clearInterval(iv); };
  }, [pendingTask?.type, projectId]);

  const acceptFact = async (id, edited) => {
    await api.post(`${API}/projects/${projectId}/fact-candidates/${id}/accept`, edited || {}).catch(() => {});
    setFactCandidates(p => p.filter(f => f.id !== id));
    setFactStats(s => ({ pending: Math.max(0, s.pending - 1), confirmed: s.confirmed + 1 }));
  };
  const rejectFact = async (id) => {
    await api.post(`${API}/projects/${projectId}/fact-candidates/${id}/reject`).catch(() => {});
    setFactCandidates(p => p.filter(f => f.id !== id));
    setFactStats(s => ({ ...s, pending: Math.max(0, s.pending - 1) }));
  };
  const acceptAllFacts = async () => {
    const n = factCandidates.length;
    await api.post(`${API}/projects/${projectId}/fact-candidates/accept-all`).catch(() => {});
    setFactCandidates([]);
    setFactStats(s => ({ pending: 0, confirmed: s.confirmed + n }));
  };
  const rejectAllFacts = async () => {
    await api.post(`${API}/projects/${projectId}/fact-candidates/reject-all`).catch(() => {});
    setFactCandidates([]);
    setFactStats(s => ({ ...s, pending: 0 }));
  };

  const openSettings = () => {
    setEditTitle(project?.title || '');
    setEditGenre(project?.genre || '');
    setEditFactMode(project?.fact_mode || 'auto');
    setShowSettings(true);
  };
  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const r = await api.patch(`${API}/projects/${projectId}`, {
        title: editTitle.trim() || project.title,
        genre: editGenre.trim() || null,
        fact_mode: editFactMode,
      });
      setProject(prev => ({ ...prev, title: r.data.title, genre: r.data.genre, fact_mode: r.data.fact_mode }));
      setShowSettings(false);
    } catch (e) {
      alert('保存失败：' + (e.response?.data?.error || e.message));
    } finally {
      setSavingSettings(false);
    }
  };

  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [messages]);

  const loadEpisode = async (ep) => {
    setActiveContent(null);
    setActiveEpisode(ep);
    setEpView(ep.status === 'outline' ? 'outline' : 'script');
    const r = await api.get(`${API}/projects/${projectId}/episodes/${ep.episode_number}`).catch(() => null);
    setEpisodeContent(r?.data || ep);
  };

  function handleBibleSaved(key, value) {
    setProject(prev => ({
      ...prev,
      bible: { ...prev.bible, [key]: value },
    }));
  }

  function guessAgentType(text) {
    if (/写第\s*\d+\s*集|写这集|这集.*剧本|剧本.*正文/.test(text)) return 'script';
    if (/大纲|分集规划|钩子|hook|beats/.test(text)) return 'outline';
    if (/一致性|检查.*矛盾|前后.*矛盾|漂移/.test(text)) return 'consistency';
    if (/世界观|人物设定|故事规则|伏笔|角色.*设定/.test(text)) return 'bible';
    return null;
  }

  const sendMessage = async (override) => {
    const text = (override?.text ?? input).trim();
    if (!text || chatLoading) return;
    const hint = override?.hint ?? (agentTypeHint || guessAgentType(text));
    const ep = (override?.episode ?? activeEpisode?.episode_number) || null;

    // 大集数大纲：先确认（避免用户不确定它要干嘛、要等多久）。≥15 集才弹。
    const oc = outlineCount(text);
    if (oc && oc >= 15 && hint !== 'script' && !override?.confirmed) {
      if (!window.confirm(`将生成 ${oc} 集分集大纲（只是大纲框架，不是逐集正文），预计 1-2 分钟。确认开始？`)) return;
    }
    // 进度气泡任务态：大纲显示集数，写正文显示集号
    const epInText = (text.match(/第\s*(\d+)\s*集/) || [])[1];
    const task = oc ? { type: 'outline', count: oc }
      : (hint === 'script' ? { type: 'script', ep: epInText || ep || '?' } : null);

    if (!override) setInput('');
    setAgentTypeHint(null);
    setPendingTask(task);
    setMessages(prev => [...prev, { role: 'user', content: text, created_at: new Date().toISOString() }]);
    setChatLoading(true);
    try {
      const r = await api.post(`${API}/projects/${projectId}/chat`, {
        message: text,
        episode: ep,
        ...(hint ? { agentTypeHint: hint } : {}),
      });
      setMessages(prev => [...prev, { role: 'agent', content: r.data.reply, agent_type: r.data.agentType, id: r.data.msgId, created_at: new Date().toISOString() }]);
      if (r.data.guidance) setProject(prev => prev ? { ...prev, guidance: r.data.guidance } : prev);

      // 门禁拦下：重写仍 critical，未覆盖原稿 → 进入"原稿 vs 候选稿"对比
      if (r.data.blocked && r.data.candidate) {
        setPendingCandidate({ ...r.data.candidate, issues: r.data.issues || [] });
        setActiveContent(null);
      }

      const { stateUpdate } = r.data;
      if (stateUpdate) {
        if (stateUpdate.bible || stateUpdate.episodes) {
          await loadProject();
          if (stateUpdate.episodes) {
            setShowRightPanel(true); // 生成大纲后确保左栏可见
          }
        }
        if (stateUpdate.treatment) setActiveContent({ type: 'treatment' });
        if (stateUpdate.updatedEpisode) {
          // 关键：把激活集切到刚生成的这一集，避免后续编辑/自动保存写错集
          setActiveEpisode(stateUpdate.updatedEpisode);
          setEpisodeContent(stateUpdate.updatedEpisode);
          setActiveContent(null);
          setEpView('script'); // 刚写完正文，中栏切到"正文"视图
          await loadProject();
          loadFacts(); // 本集可能抽出新的待确认事实
        }
        if (stateUpdate.consistencyIssues) {
          setConsistencyIssues(stateUpdate.consistencyIssues);
        }
        if (stateUpdate.autoConsistencyReply) {
          setMessages(prev => [...prev, {
            role: 'agent',
            content: stateUpdate.autoConsistencyReply,
            agent_type: 'consistency',
            created_at: new Date().toISOString(),
          }]);
        }
      }
    } catch (e) {
      setMessages(prev => [...prev, { role: 'agent', content: `提示：${e.response?.data?.error || e.message}`, created_at: new Date().toISOString() }]);
    } finally {
      setChatLoading(false);
      setPendingTask(null);
    }
  };

  function handleWriteEpisode(epNum) {
    setAgentTypeHint('script');
    setInput(`帮我写第${epNum}集的剧本正文`);
    setTimeout(() => inputRef.current?.focus(), 50);
  }

  // 编辑器内容变化时防抖保存
  const handleEditorSave = useCallback((text, html) => {
    if (!activeEpisode) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      await api.put(`${API}/projects/${projectId}/episodes/${activeEpisode.episode_number}`, { content: text }).catch(() => {});
    }, 1500);
  }, [activeEpisode, projectId]);

  // 改集名
  const renameEpisode = useCallback(async (epNum, newTitle) => {
    if (!newTitle) return;
    try {
      await api.put(`${API}/projects/${projectId}/episodes/${epNum}`, { title: newTitle });
      setEpisodeContent(prev => prev && prev.episode_number === epNum ? { ...prev, title: newTitle } : prev);
      setActiveEpisode(prev => prev && prev.episode_number === epNum ? { ...prev, title: newTitle } : prev);
      setProject(prev => prev ? { ...prev, episodes: (prev.episodes || []).map(e => e.episode_number === epNum ? { ...e, title: newTitle } : e) } : prev);
    } catch (e) { alert('改名失败：' + (e.response?.data?.error || e.message)); }
  }, [projectId]);

  // 手动保存故事大纲(treatment)
  const saveTreatment = useCallback(async (text) => {
    await api.patch(`${API}/projects/${projectId}/bible`, { key: 'treatment', value: text });
    setProject(prev => prev ? { ...prev, bible: { ...prev.bible, treatment: text } } : prev);
    loadProject(); // 刷新引导（故事大纲完成度→步骤条）
  }, [projectId, loadProject]);

  // 门禁对比：采用候选稿 / 保留原稿
  const acceptCandidate = useCallback(async () => {
    if (!pendingCandidate) return;
    const ep = pendingCandidate.episode_number;
    try {
      await api.post(`${API}/projects/${projectId}/episodes/${ep}/accept-candidate`, {
        title: pendingCandidate.title, content: pendingCandidate.content,
        summary: pendingCandidate.summary, end_state: pendingCandidate.end_state, issues: pendingCandidate.issues,
      });
      setPendingCandidate(null);
      await loadProject();
      const r = await api.get(`${API}/projects/${projectId}/episodes/${ep}`).catch(() => null);
      if (r?.data) { setActiveEpisode(r.data); setEpisodeContent(r.data); }
    } catch (e) { alert('采用候选失败：' + (e.response?.data?.error || e.message)); }
  }, [pendingCandidate, projectId, loadProject]);

  // 首页直达：项目加载完成后，把用户的想法自动发给小影（只发一次），落地即见 AI 回应
  const autoSentRef = useRef(false);
  useEffect(() => {
    if (project && autoSend && !autoSentRef.current) {
      autoSentRef.current = true;
      sendMessage({ text: autoSend });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project, autoSend]);

  if (!project) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 size={20} className="animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="flex flex-row-reverse h-full overflow-hidden bg-[#f6f3ec] dark:bg-[#0e0e10]">
      {/* ─── 对话区（row-reverse 后视觉在右侧 = 小影栏）─── */}
      <div className="shrink-0 flex flex-col bg-[#fbf8f1] dark:bg-[#0c0c10]" style={{ width: chatWidth }}>
        {/* 顶部：小影 */}
        <div className="shrink-0 px-5 py-4 border-b border-[#ded6c8] dark:border-white/[0.06] flex items-center gap-2">
          <div className="flex h-6 w-6 items-center justify-center rounded-lg bg-[#eee9de] text-[#6f6196] dark:bg-white/[0.08] dark:text-[#b9afe7] shrink-0"><Cpu size={13} /></div>
          <span className="text-[15px] font-bold text-stone-900 dark:text-gray-200">小影</span>
          <span className="text-[11px] text-stone-400">陪你把想法写成短剧</span>
        </div>
        {/* 阶段进度（由引导大脑驱动，回退到 current_stage） */}
        {(() => {
          const FALLBACK = [
            { key:'creative', label:'创意' }, { key:'outline', label:'分集大纲' },
            { key:'writing', label:'写正文' }, { key:'review', label:'完善' },
          ];
          const g = project.guidance;
          let steps;
          if (g?.steps?.length) {
            steps = g.steps;
          } else {
            const map = { bible:'creative', outline:'outline', writing:'writing', review:'review' };
            const curKey = map[project.current_stage] || 'creative';
            const cur = FALLBACK.findIndex(s => s.key === curKey);
            steps = FALLBACK.map((s, i) => ({ key:s.key, label:s.label, done:i<cur, active:i===cur }));
          }
          return (
            <div className="shrink-0 flex items-center px-3.5 py-2 border-b border-[#e4dccf] dark:border-white/[0.04] gap-0">
              {steps.map((s, i) => (
                <div key={s.key} className="flex items-center flex-1">
                  <div className={`flex items-center justify-center gap-1.5 flex-1 ${(s.done || s.active) ? 'text-[#6f6196] dark:text-[#b9afe7]' : 'text-stone-300 dark:text-gray-600'}`}>
                    <span className={`w-2 h-2 rounded-full border shrink-0 ${s.done ? 'bg-[#8c7db0] border-[#8c7db0]' : s.active ? 'border-[#8c7db0] ring-2 ring-[#8c7db0]/15' : 'border-stone-300 dark:border-gray-600'}`} />
                    <span className={`text-[10px] ${s.active ? 'font-bold' : 'font-semibold'}`}>{s.label}</span>
                  </div>
                  {i < steps.length - 1 && (
                    <div className={`h-px w-3 shrink-0 ${s.done ? 'bg-[#8c7db0]' : 'bg-[#ded6c8] dark:bg-white/[0.08]'}`} />
                  )}
                </div>
              ))}
            </div>
          );
        })()}

        {/* 消息列表 */}
        <div ref={chatRef} className="flex-1 overflow-y-auto p-5 space-y-4">
          {messages.length === 0 && (
            <div className="flex justify-center py-8">
              <Loader2 size={16} className="animate-spin text-gray-300" />
            </div>
          )}
          {messages.map((m, i) => {
            const isUser = m.role === 'user';
            const badge = !isUser && m.agent_type ? AGENT_LABEL[m.agent_type] : null;
            const msgId = m.id;
            return (
              <div key={i} className={`flex flex-col ${isUser ? 'items-end' : 'items-start'}`}>
                <div className={`max-w-[88%] text-[13px] leading-relaxed rounded-lg px-4 py-3 ${
                  isUser
                    ? 'bg-[#8c7db0] text-white rounded-tr-sm'
                    : 'bg-white/70 dark:bg-white/[0.05] border border-[#ded6c8] dark:border-white/[0.07] text-stone-800 dark:text-gray-300 rounded-tl-sm'
                }`}>
                  {badge && <div className="mb-1"><Badge color={badge.color}>{badge.label}</Badge></div>}
                  <p className="whitespace-pre-wrap">{(isUser ? m.content : normalizeAssistantUiText(m.content)).replace(/\\n/g, '\n')}</p>
                </div>
                {!isUser && msgId && (
                  <div className="flex gap-1 mt-1 ml-1">
                    <button
                      onClick={async () => {
                        await api.post(`${API}/projects/${projectId}/chat/${msgId}/rate`, { rating: 5 }).catch(() => {});
                        setMessages(prev => prev.map((msg, j) => j === i ? { ...msg, rating: 5 } : msg));
                      }}
                      className={`p-1 rounded-md transition-colors ${m.rating === 5 ? 'text-[#5e7c76]' : 'text-gray-300 hover:text-[#5e7c76]'}`}
                      title="好评"
                    >
                      <ThumbsUp size={11} />
                    </button>
                    <button
                      onClick={async () => {
                        await api.post(`${API}/projects/${projectId}/chat/${msgId}/rate`, { rating: 1 }).catch(() => {});
                        setMessages(prev => prev.map((msg, j) => j === i ? { ...msg, rating: 1 } : msg));
                      }}
                      className={`p-1 rounded-md transition-colors ${m.rating === 1 ? 'text-red-400' : 'text-gray-300 hover:text-red-400'}`}
                      title="差评"
                    >
                      <ThumbsDown size={11} />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
          {chatLoading && <ThinkingBubble task={pendingTask} />}
        </div>

        {/* 项目设置弹窗 */}
        {showSettings && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowSettings(false)}>
            <div onClick={e => e.stopPropagation()}
              className="w-[340px] bg-[#fbfaf6] dark:bg-[#1a1a22] rounded-2xl border border-[#ded6c8] dark:border-white/[0.08] shadow-2xl overflow-hidden">
              <div className="flex items-center gap-2 px-4 py-3 border-b border-[#e4dccf] dark:border-white/[0.06]">
                <Settings size={14} className="text-[#6f6196]" />
                <span className="text-[13px] font-semibold text-gray-800 dark:text-gray-200 flex-1">项目设置</span>
                <button onClick={() => setShowSettings(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                  <X size={15} />
                </button>
              </div>
              <div className="p-4 space-y-3">
                <div>
                  <p className="text-[11px] text-gray-400 mb-1.5">标题</p>
                  <input value={editTitle} onChange={e => setEditTitle(e.target.value)}
                    className="w-full bg-[#f7f3eb] dark:bg-white/[0.05] border border-[#ded6c8] dark:border-white/[0.08] rounded-xl px-3 py-2 text-[13px] text-gray-800 dark:text-gray-200 outline-none focus:border-[#8c7db0]" />
                </div>
                <div>
                  <p className="text-[11px] text-gray-400 mb-1.5">题材（可自由输入，如「赛博朋克」「科幻废土」）</p>
                  <input value={editGenre} onChange={e => setEditGenre(e.target.value)}
                    placeholder="输入或点选下方标签"
                    className="w-full bg-[#f7f3eb] dark:bg-white/[0.05] border border-[#ded6c8] dark:border-white/[0.08] rounded-xl px-3 py-2 text-[13px] text-gray-800 dark:text-gray-200 placeholder:text-gray-400 outline-none focus:border-[#8c7db0]" />
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {['古风穿越', '现代甜宠', '悬疑推理', '都市爽文', '职场逆袭', '家庭伦理', '赛博朋克', '科幻废土'].map(g => (
                      <button key={g} onClick={() => setEditGenre(g)}
                        className={`px-2.5 py-1 text-[11px] rounded-lg border transition-all ${
                          editGenre === g
                            ? 'bg-[#8c7db0] border-[#8c7db0] text-white'
                            : 'bg-[#f7f3eb] dark:bg-white/[0.04] border-[#ded6c8] dark:border-white/[0.08] text-gray-500 dark:text-gray-400 hover:border-[#c9bedc]'
                        }`}>
                        {g}
                      </button>
                    ))}
                  </div>
                </div>
                {/* 事实确认模式 */}
                <div>
                  <p className="text-[11px] text-gray-400 mb-1.5">事实确认模式</p>
                  <div className="flex gap-1.5">
                    {[['auto','自动','高置信(≥85%)自动入库，其余才需你审'], ['manual','手动','每条抽取的事实都要你确认']].map(([val, label, desc]) => (
                      <button key={val} onClick={() => setEditFactMode(val)}
                        className={`flex-1 px-2 py-2 rounded-xl border text-left transition-all ${
                          editFactMode === val
                            ? 'bg-[#eee9de] dark:bg-white/[0.08] border-[#8c7db0] dark:border-white/[0.16]'
                            : 'bg-[#f7f3eb] dark:bg-white/[0.04] border-[#ded6c8] dark:border-white/[0.08] hover:border-[#c9bedc]'
                        }`}>
                        <p className={`text-[12px] font-semibold ${editFactMode===val ? 'text-[#6f6196] dark:text-[#b9afe7]' : 'text-gray-600 dark:text-gray-300'}`}>{label}</p>
                        <p className="text-[10px] text-gray-400 mt-0.5 leading-tight">{desc}</p>
                      </button>
                    ))}
                  </div>
                </div>
                <p className="text-[11px] text-[#7f6b45] leading-relaxed">改题材只更新项目设定，不会动你已写的剧本正文；改完一致性检查就不会再报题材不符了。</p>
                <div className="flex gap-2 pt-1">
                  <button onClick={() => setShowSettings(false)}
                    className="px-4 py-2 rounded-xl border border-[#ded6c8] dark:border-white/[0.1] text-gray-600 dark:text-gray-300 text-[13px] hover:bg-[#f7f3eb] dark:hover:bg-white/[0.04]">
                    取消
                  </button>
                  <button onClick={saveSettings} disabled={savingSettings || !editTitle.trim()}
                    className="flex-1 py-2 rounded-xl bg-[#8c7db0] hover:bg-[#7d6ca3] disabled:opacity-40 text-white text-[13px] font-medium flex items-center justify-center gap-2">
                    {savingSettings ? <Loader2 size={13} className="animate-spin" /> : null}保存
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 输入框 */}
        <div className="shrink-0 border-t border-[#ded6c8] dark:border-white/[0.06] bg-[#fbf8f1] dark:bg-[#0c0c10]">
          {/* 引导：下一步建议 + 完成度（由 guidance 驱动） */}
          {!chatLoading && project.guidance && (() => {
            const g = project.guidance;
            return (
              <div className="px-3 pt-2.5 pb-1 space-y-1.5">
                {/* 下一步一句话 + 完成度清单 */}
                {g.hint && (
                  <div className="flex items-start gap-1.5">
                    <span className="text-[10px] text-[#6f6196] dark:text-[#b9afe7] shrink-0 mt-px">下一步</span>
                    <p className="text-[10px] text-gray-500 dark:text-gray-400 leading-relaxed">{g.hint}</p>
                  </div>
                )}
                {Array.isArray(g.checklist) && g.checklist.length > 0 && (
                  <div className="flex gap-1.5 flex-wrap">
                    {g.checklist.map((c, i) => (
                      <span key={i} className={`text-[10px] inline-flex items-center gap-0.5 ${c.done ? 'text-[#5e7c76]' : 'text-gray-400 dark:text-gray-600'}`}>
                        {c.done ? '✓' : '○'} {c.label}
                      </span>
                    ))}
                  </div>
                )}
                {/* 建议按钮：点击直接发送（带 agentTypeHint + episode，绕过盲判） */}
                {Array.isArray(g.suggestions) && g.suggestions.length > 0 && (
                  <div className="flex gap-1.5 flex-wrap pt-0.5">
                    {g.suggestions.map((s, i) => (
                      <button
                        key={i}
                        onClick={() => sendMessage({ text: s.label, hint: s.agentType, episode: s.episode })}
                        className="px-2 py-1 text-[10px] bg-[#eee9de] dark:bg-white/[0.08] border border-[#ded6c8] dark:border-white/[0.12] text-[#6f6196] dark:text-[#b9afe7] rounded-lg hover:bg-[#e5dece] transition-all"
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}
          <div className="flex gap-2 items-end p-5 pt-2">
            <textarea
              rows={2}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
              ref={inputRef}
              placeholder={activeEpisode ? `针对第${activeEpisode.episode_number}集…` : '告诉写作助手你想做什么…'}
              className="flex-1 resize-none bg-[#fbfaf6]/90 dark:bg-white/[0.05] border border-[#d9d0c2] dark:border-white/[0.08] rounded-lg px-4 py-3 text-[14px] text-stone-800 dark:text-gray-200 placeholder:text-stone-400 outline-none focus:border-[#8c7db0] max-h-24"
            />
            <button
              onClick={sendMessage}
              disabled={!input.trim() || chatLoading}
              className="w-10 h-10 bg-[#8c7db0] hover:bg-[#7d6ca3] disabled:opacity-40 text-white rounded-lg flex items-center justify-center shrink-0 transition-colors mb-0.5 shadow-lg shadow-[#8c7db0]/20"
            >
              {chatLoading ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
            </button>
          </div>
        </div>
      </div>

      {/* 左拖拽手柄 */}
      <div onMouseDown={e => startDrag('left', e)} className="w-2 shrink-0 cursor-col-resize relative hover:bg-[#eee9de] dark:hover:bg-white/[0.08] transition-colors">
        <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-px bg-gray-200 dark:bg-white/[0.06]" />
      </div>

      {/* ─── 中栏：编辑器 ─── */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* 工具条：返回 + 当前编辑对象 + 大纲/正文 + 下载 + 设置 */}
        <div className="shrink-0 flex items-center gap-2 px-4 py-2.5 border-b border-[#ded6c8] dark:border-white/[0.06] bg-[#fbfaf6] dark:bg-[#0c0c10]">
          <button onClick={onBack} title="返回作品列表" className="p-1 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors shrink-0"><ChevronLeft size={16} /></button>
          <div className="flex-1 min-w-0">
            {activeContent ? (
              <button onClick={() => setActiveContent(null)} title="返回本集" className="max-w-full truncate text-[13px] font-semibold text-stone-800 dark:text-gray-200 hover:text-[#6f6196] transition-colors">
                {activeContent.type === 'bible' ? activeContent.label
                  : activeContent.type === 'character' ? (project.bible?.characters?.[activeContent.index]?.name || '人物小传')
                  : activeContent.type === 'treatment' ? '故事大纲'
                  : '分集大纲'}
                <span className="ml-1.5 text-[11px] font-normal text-gray-400">· 返回本集</span>
              </button>
            ) : episodeContent ? (
              <p className="truncate text-[13px] font-semibold text-stone-800 dark:text-gray-200">第{episodeContent.episode_number}集《{episodeContent.title || '未命名'}》</p>
            ) : (
              <p className="truncate text-[13px] text-gray-400">{project.title}</p>
            )}
          </div>
          {!activeContent && episodeContent && (
            <div className="flex rounded-lg border border-[#ded6c8] dark:border-white/[0.08] overflow-hidden shrink-0">
              {[['outline', '大纲'], ['script', '正文']].map(([v, l]) => (
                <button key={v} onClick={() => setEpView(v)}
                  className={`px-3 py-1 text-[12px] font-medium transition-colors ${epView === v ? 'bg-[#8c7db0] text-white' : 'text-gray-500 dark:text-gray-400 hover:bg-[#f5f0e7] dark:hover:bg-white/[0.04]'}`}>{l}</button>
              ))}
            </div>
          )}
          <button
            onClick={async () => {
              const eps = (project.episodes || []).filter(e => e.status !== 'outline');
              if (eps.length === 0) { alert('还没有剧本正文，先让小影写几集吧'); return; }
              try {
                const r = await api.get(`${API}/projects/${project.id}/download`, { responseType: 'blob' });
                const url = URL.createObjectURL(r.data);
                const a = document.createElement('a');
                a.href = url; a.download = `${project.title}.docx`; a.click();
                URL.revokeObjectURL(url);
              } catch (e) { alert('下载失败：' + e.message); }
            }}
            title="下载剧本 DOCX" className="p-1.5 text-gray-400 hover:text-[#6f6196] transition-colors shrink-0"><Download size={14} /></button>
          <button onClick={openSettings} title="项目设置（改标题/题材）" className="p-1.5 text-gray-400 hover:text-[#6f6196] transition-colors shrink-0"><Settings size={14} /></button>
        </div>

        {/* 编辑器主体 */}
        <div className="flex-1 min-h-0 flex flex-col">
          {pendingCandidate
            ? <CandidateCompareView projectId={projectId} candidate={pendingCandidate} onAccept={acceptCandidate} onKeep={() => setPendingCandidate(null)} />
            : activeContent?.type === 'bible'
            ? <BibleFieldEditor field={activeContent} projectId={projectId} onSaved={handleBibleSaved} />
            : activeContent?.type === 'character'
              ? <CharacterEditor key={activeContent.index} charIndex={activeContent.index} projectId={projectId} bible={project.bible} onSaved={handleBibleSaved} />
              : activeContent?.type === 'treatment'
                ? <TreatmentView treatment={project.bible?.treatment} onSave={saveTreatment} />
                : activeContent?.type === 'story-outline'
                ? <StoryOutlineView episodes={project.episodes || []} />
                : episodeContent
                  ? (epView === 'outline'
                      ? <OutlineView episode={episodeContent} onWrite={handleWriteEpisode} />
                      : <ScriptEditor episode={episodeContent} onSave={handleEditorSave} onRename={renameEpisode} />)
                  : <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">从左边选一集，或让小影帮你规划大纲</div>}
        </div>
      </div>

      {showRightPanel && <>
        {/* 右拖拽手柄 */}
        <div onMouseDown={e => startDrag('right', e)} className="w-2 shrink-0 cursor-col-resize relative hover:bg-[#eee9de] dark:hover:bg-white/[0.08] transition-colors">
          <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-px bg-gray-200 dark:bg-white/[0.06]" />
        </div>

        {/* ─── 右栏：项目状态 ─── */}
        <div className="shrink-0 flex flex-col bg-[#f4f1e9] dark:bg-[#0a0a0d]" style={{ width: leftWidth }}>

        {/* 集数（常驻） */}
        <div className="shrink-0 max-h-[46%] overflow-y-auto p-3 space-y-2 border-b border-[#ded6c8] dark:border-white/[0.06]">
          <p className="text-[10px] font-semibold text-gray-400 px-1 flex items-center gap-1"><List size={11} />集数</p>
          <>
              {(project.episodes || []).length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-8">还没有集数，让写作助手帮你规划大纲</p>
              ) : (
                project.episodes.map(ep => {
                  const st = EP_STATUS_MAP[ep.status] || { label: ep.status, color: 'gray' };
                  const isActive = activeEpisode?.episode_number === ep.episode_number;
                  return (
                    <button
                      key={ep.episode_number}
                      onClick={() => loadEpisode(ep)}
                      className={`w-full text-left p-2.5 rounded-xl border transition-all ${
                        isActive
                          ? 'bg-[#eee9de] border-[#c9bedc] dark:bg-white/[0.08] dark:border-white/[0.16]'
                          : 'bg-[#fbfaf6] dark:bg-[#14141a] border-[#ded6c8] dark:border-white/[0.06] hover:border-[#c9bedc] dark:hover:border-white/[0.12]'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-0.5">
                        <span className="text-[11px] font-semibold text-gray-700 dark:text-gray-300">第{ep.episode_number}集</span>
                        <Badge color={st.color}>{st.label}</Badge>
                      </div>
                      <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">{ep.title || '未命名'}</p>
                      {ep.summary && <p className="text-[10px] text-gray-400 mt-0.5 line-clamp-2 leading-snug">{ep.summary}</p>}
                    </button>
                  );
                })
              )}

              {/* 一致性检查问题 */}
              {consistencyIssues.length > 0 && (
                <div className="mt-2 pt-2 border-t border-[#ded6c8] dark:border-white/[0.06]">
                  <p className="text-[10px] font-semibold text-red-500 mb-2 flex items-center gap-1"><AlertTriangle size={10} />一致性问题</p>
                  {consistencyIssues.map((issue, i) => (
                    <div key={i} className="bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-lg p-2 mb-1.5">
                      <p className="text-[10px] font-semibold text-red-600 dark:text-red-400">ep{issue.episode} · {issue.type}</p>
                      <p className="text-[10px] text-red-500 dark:text-red-400 mt-0.5 leading-snug">{issue.description}</p>
                    </div>
                  ))}
                </div>
              )}
          </>
        </div>

        {/* 全剧设定 / 事实 子tab */}
        <div className="shrink-0 flex border-b border-[#ded6c8] dark:border-white/[0.06]">
          {[['creative', '全剧设定'], ['facts', '事实']].map(([key, label]) => (
            <button key={key} onClick={() => setSettingTab(key)}
              className={`flex-1 py-2 text-[11px] font-semibold transition-colors relative ${
                settingTab === key
                  ? 'text-[#6f6196] dark:text-[#b9afe7] border-b-2 border-[#8c7db0] bg-[#fbfaf6] dark:bg-[#0c0c10]'
                  : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
              }`}>
              {label}
              {key === 'facts' && factStats.pending > 0 && (
                <span className="ml-1 inline-flex items-center justify-center min-w-[14px] h-[14px] px-1 rounded-full bg-[#c2a079] text-white text-[9px] font-bold align-middle">{factStats.pending}</span>
              )}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-3 space-y-2">

          {/* 创意设定 */}
          {settingTab === 'creative' && (() => {
            const b = project.bible || {};
            const chars = Array.isArray(b.characters) ? b.characters : [];
            const fores = Array.isArray(b.foreshadowing) ? b.foreshadowing : [];
            const eps = project.episodes || [];
            const cardBase = 'w-full text-left bg-[#fbfaf6] dark:bg-[#14141a] border rounded-xl p-2.5 transition-all hover:border-[#c9bedc] cursor-pointer';
            const cardActive = 'border-[#8c7db0] dark:border-white/[0.16] ring-1 ring-[#c9bedc] dark:ring-white/[0.12]';
            const cardIdle = 'border-[#ded6c8] dark:border-white/[0.07]';
            return (
              <div className="space-y-2">
                {/* 世界观 */}
                <button onClick={() => setActiveContent({ type:'bible', key:'worldview', label:'世界观', value: b.worldview||'' })}
                  className={`${cardBase} ${activeContent?.key==='worldview' ? cardActive : cardIdle}`}>
                  <p className="text-[10px] font-semibold text-gray-400 mb-1 flex items-center gap-1"><Globe2 size={10} />世界观</p>
                  {b.worldview
                    ? <p className="text-[11px] text-gray-700 dark:text-gray-300 leading-snug line-clamp-3">{b.worldview}</p>
                    : <p className="text-[11px] text-gray-400 italic">点击编辑…</p>}
                </button>
                {/* 核心矛盾 */}
                <button onClick={() => setActiveContent({ type:'bible', key:'main_conflict', label:'核心矛盾', value: b.main_conflict||'' })}
                  className={`${cardBase} ${activeContent?.key==='main_conflict' ? cardActive : cardIdle}`}>
                  <p className="text-[10px] font-semibold text-gray-400 mb-1 flex items-center gap-1"><Swords size={10} />核心矛盾</p>
                  {b.main_conflict
                    ? <p className="text-[11px] text-gray-700 dark:text-gray-300 leading-snug line-clamp-3">{b.main_conflict}</p>
                    : <p className="text-[11px] text-gray-400 italic">点击编辑…</p>}
                </button>
                {/* 故事大纲（treatment） */}
                <button onClick={() => setActiveContent({ type:'treatment' })}
                  className={`${cardBase} ${activeContent?.type==='treatment' ? cardActive : cardIdle}`}>
                  <p className="text-[10px] font-semibold text-gray-400 mb-1 flex items-center gap-1"><Map size={10} />故事大纲</p>
                  {b.treatment
                    ? <p className="text-[11px] text-gray-700 dark:text-gray-300 leading-snug line-clamp-3">{b.treatment}</p>
                    : <p className="text-[11px] text-gray-400 italic">还没生成 · 点击查看</p>}
                </button>
                {/* 故事大纲（集数聚合） */}
                {eps.length > 0 && (
                  <button onClick={() => setActiveContent({ type:'story-outline' })}
                    className={`${cardBase} ${activeContent?.type==='story-outline' ? cardActive : cardIdle}`}>
                    <p className="text-[10px] font-semibold text-gray-400 mb-1 flex items-center gap-1"><List size={10} />分集大纲</p>
                    <p className="text-[11px] text-gray-700 dark:text-gray-300">共 {eps.length} 集 · 点击查看完整大纲</p>
                  </button>
                )}
                {/* 人物小传 */}
                {chars.length > 0 && (
                  <div className="bg-[#fbfaf6] dark:bg-[#14141a] border border-[#ded6c8] dark:border-white/[0.07] rounded-xl p-2.5">
                    <p className="text-[10px] font-semibold text-gray-400 mb-2 flex items-center gap-1"><Users size={10} />人物小传</p>
                    {chars.map((c, i) => (
                      <button key={i} onClick={() => setActiveContent({ type:'character', index: i })}
                        className={`w-full text-left mb-1.5 last:mb-0 pb-1.5 last:pb-0 border-b border-gray-100 dark:border-gray-800 last:border-0 hover:opacity-70 transition-opacity ${activeContent?.type==='character' && activeContent?.index===i ? 'opacity-60' : ''}`}>
                        <p className="text-[11px] font-semibold text-gray-800 dark:text-gray-200">{c.name} <span className="font-normal text-gray-400">({c.role})</span></p>
                        {c.dramatic_tag && <p className="text-[10px] text-[#6f6196] mt-0.5">{c.dramatic_tag}</p>}
                        {!c.dramatic_tag && c.personality?.length > 0 && <p className="text-[10px] text-gray-400 mt-0.5">{c.personality.join(' · ')}</p>}
                      </button>
                    ))}
                  </div>
                )}
                {/* 伏笔 */}
                {fores.length > 0 && (
                  <div className="bg-[#fbfaf6] dark:bg-[#14141a] border-l-4 border-l-[#c2a079] border-y border-y-[#ded6c8] border-r border-r-[#ded6c8] dark:border-y-white/[0.07] dark:border-r-white/[0.07] rounded-xl p-2.5">
                    <p className="text-[10px] font-semibold text-[#7f6b45] mb-1.5 flex items-center gap-1"><Search size={10} />伏笔</p>
                    {fores.map((f, i) => (
                      <div key={i} className="mb-1.5 last:mb-0">
                        <p className="text-[10px] font-semibold text-gray-700 dark:text-gray-300">{f.title}</p>
                        <p className="text-[10px] text-gray-400 leading-snug">{f.desc}</p>
                      </div>
                    ))}
                  </div>
                )}
                {!b.worldview && !b.main_conflict && chars.length === 0 && (
                  <p className="text-xs text-gray-400 text-center py-8">还没有创意设定，跟写作助手说说你的故事想法吧</p>
                )}
              </div>
            );
          })()}

          {/* 事实面板：待确认事实 + 已确认计数 */}
          {settingTab === 'facts' && (() => {
            const ET = { character:'人物', prop:'道具', location:'地点', relationship:'关系', timeline_event:'事件', knowledge_state:'认知', world_rule:'规则', foreshadowing:'伏笔' };
            return (
              <div className="space-y-2">
                <div className="flex items-center gap-2 px-1">
                  <span className="text-[11px] text-gray-500">已确认 <b className="text-[#5e7c76]">{factStats.confirmed}</b> 条</span>
                  <span className="text-[11px] text-gray-500">待确认 <b className="text-[#7f6b45]">{factStats.pending}</b> 条</span>
                  <span className="ml-auto text-[10px] text-gray-400">{project.fact_mode === 'manual' ? '手动模式' : '自动模式'}</span>
                </div>
                {factCandidates.length > 0 && (
                  <div className="flex gap-1.5 px-1">
                    <button onClick={acceptAllFacts}
                      className="flex-1 py-1 rounded-lg bg-[#e7eee8] text-[#5e7c76] dark:bg-white/[0.08] dark:text-[#9fc6bd] text-[10px] font-medium hover:bg-[#dbe8df]">全部接受 ({factCandidates.length})</button>
                    <button onClick={rejectAllFacts}
                      className="flex-1 py-1 rounded-lg bg-[#eee9de] dark:bg-white/[0.05] text-gray-500 text-[10px] font-medium hover:bg-[#e5dece] dark:hover:bg-white/[0.08]">全部拒绝</button>
                  </div>
                )}
                <p className="text-[10px] text-gray-400 px-1 leading-relaxed">确认过的事实会成为后续写作和门禁的硬基准，跨集强制一致。{project.fact_mode === 'manual' ? '手动模式：每条都需确认。' : '自动模式：高置信已自动入库，这里只剩需你判断的。'}</p>
                {factCandidates.length === 0 ? (
                  <p className="text-xs text-gray-400 text-center py-8">暂无待确认事实。写新集后会自动抽取。</p>
                ) : factCandidates.map(f => (
                  <div key={f.id} className="bg-[#fbfaf6] dark:bg-[#14141a] border border-[#ded6c8] dark:border-white/[0.07] rounded-xl p-2.5">
                    <div className="flex items-center gap-1.5 mb-1">
                      <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-[#eee9de] dark:bg-white/[0.08] text-[#6f6196] dark:text-[#b9afe7]">{ET[f.entity_type] || f.entity_type}</span>
                      <span className="text-[11px] font-semibold text-gray-800 dark:text-gray-200">{f.entity_name}</span>
                      {f.episode_number && <span className="text-[9px] text-gray-400">ep{f.episode_number}</span>}
                      {f.confidence != null && <span className="ml-auto text-[9px] text-gray-400">{Math.round(f.confidence * 100)}%</span>}
                    </div>
                    <p className="text-[11px] text-gray-700 dark:text-gray-300 leading-snug">{f.value}</p>
                    {f.source_quote && <p className="text-[10px] text-gray-400 italic mt-0.5 line-clamp-2">「{f.source_quote}」</p>}
                    <div className="flex gap-1.5 mt-2">
                      <button onClick={() => acceptFact(f.id)}
                        className="flex-1 py-1 rounded-lg bg-[#e7eee8] text-[#5e7c76] dark:bg-white/[0.08] dark:text-[#9fc6bd] text-[10px] font-medium hover:bg-[#dbe8df] transition-colors">接受</button>
                      <button onClick={() => rejectFact(f.id)}
                        className="flex-1 py-1 rounded-lg bg-[#eee9de] dark:bg-white/[0.05] text-gray-500 text-[10px] font-medium hover:bg-[#e5dece] dark:hover:bg-white/[0.08] transition-colors">拒绝</button>
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
        </div>
      </>}
    </div>
  );
}

// ── 主页面入口 ─────────────────────────────────────────────
export default function AgentLab() {
  const [openProject, setOpenProject] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const initialIdea = searchParams.get('idea') || '';
  const initialBrief = searchParams.get('brief') || '';
  const initialTab = searchParams.get('tab') || 'scripts';
  const openId = searchParams.get('open');
  const sendMsg = searchParams.get('send') || '';

  // 首页直达：?open=<项目id>&send=<想法> → 自动打开该项目 + 进去自动发第一条给小影
  useEffect(() => {
    if (openId && openProject?.id !== openId) setOpenProject({ id: openId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openId]);

  if (openProject) {
    return (
      <ProjectWorkspace
        projectId={openProject.id}
        autoSend={openProject.id === openId ? sendMsg : ''}
        onBack={() => { setOpenProject(null); setSearchParams({}); }}
      />
    );
  }

  return <ProjectList onOpen={setOpenProject} initialIdea={initialIdea} initialBrief={initialBrief} initialTab={initialTab} />;
}
